import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Globe, Bot } from 'lucide-react';
import api from '../../api';

const CouncilSettings: React.FC = () => {
  const [webSearch, setWebSearch] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [models, setModels] = useState<{ id: string; name: string; provider: string; isPresident?: boolean }[]>([]);
  const [disabledModels, setDisabledModels] = useState<string[]>([]);
  const [modelSaving, setModelSaving] = useState<string | null>(null);
  const [presidentModel, setPresidentModel] = useState<string>('');
  const [presidentOptions, setPresidentOptions] = useState<{ id: string; name: string }[]>([]);
  const [presidentSaving, setPresidentSaving] = useState(false);

  useEffect(() => {
    api.get('/api/ai-council/tenant-config').then(res => {
      setWebSearch(res.data.data?.web_search_enabled !== false);
      setDisabledModels(Array.isArray(res.data.data?.disabled_models) ? res.data.data.disabled_models : []);
    }).catch(() => {});
    api.get('/api/ai-council/models', { params: { all: true } }).then(res => {
      setModels((res.data.models || []).filter((m: any) => !m.isPresident));
      setPresidentOptions(Array.isArray(res.data.presidentModels) ? res.data.presidentModels : []);
      if (res.data.configuredPresidentModel) setPresidentModel(res.data.configuredPresidentModel);
    }).catch(() => {});
  }, []);

  const toggleWebSearch = async () => {
    setConfigLoading(true);
    try {
      const newVal = !webSearch;
      await api.patch('/api/ai-council/tenant-config', { web_search_enabled: newVal });
      setWebSearch(newVal);
      toast.success(`Web search ${newVal ? 'enabled' : 'disabled'} for this organization`);
    } catch {
      toast.error('Failed to update setting');
    } finally {
      setConfigLoading(false);
    }
  };

  const savePresident = async (modelId: string) => {
    const prev = presidentModel;
    setPresidentModel(modelId);
    setPresidentSaving(true);
    try {
      await api.patch('/api/ai-council/tenant-config/president', { president_model: modelId });
      toast.success('Council President model updated');
    } catch (err: any) {
      setPresidentModel(prev);
      toast.error(err?.response?.data?.error || 'Failed to update President model');
    } finally {
      setPresidentSaving(false);
    }
  };

  const toggleModel = async (modelId: string) => {
    const next = disabledModels.includes(modelId)
      ? disabledModels.filter(id => id !== modelId)
      : [...disabledModels, modelId];
    setModelSaving(modelId);
    const prev = disabledModels;
    setDisabledModels(next);
    try {
      await api.patch('/api/ai-council/tenant-config/models', { disabled_models: next });
    } catch (err: any) {
      setDisabledModels(prev);
      toast.error(err?.response?.data?.error || 'Failed to update model access');
    } finally {
      setModelSaving(null);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">AI Council Settings</h2>
        <p className="text-gray-500 text-sm">Control which AI Council features are available to users in this organization.</p>
      </div>
      <div className="border border-gray-200 rounded-xl divide-y divide-gray-100">
        <div className="flex items-start justify-between p-5 gap-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-50 rounded-lg mt-0.5">
              <Globe className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Web Search</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Allow council members to search the internet for research. When disabled, the web search toggle is hidden for all users in this organization.
              </p>
              <span className={`inline-block mt-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${webSearch ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {webSearch ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
          <button
            onClick={toggleWebSearch}
            disabled={configLoading}
            className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${webSearch ? 'bg-blue-500' : 'bg-gray-300'}`}
          >
            <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${webSearch ? 'translate-x-6' : 'translate-x-0'}`} />
          </button>
        </div>
      </div>

      <div className="mt-6">
        <div className="mb-3">
          <p className="text-sm font-semibold text-gray-800">Council President Model</p>
          <p className="text-xs text-gray-500 mt-0.5">The model that synthesizes all member responses into the final combined answer. New sessions use this selection.</p>
        </div>
        <div className="border border-gray-200 rounded-xl p-5 flex items-center gap-3">
          <div className="p-2 bg-amber-50 rounded-lg flex-shrink-0">
            <Bot className="w-4 h-4 text-amber-600" />
          </div>
          <select
            value={presidentModel}
            onChange={(e) => savePresident(e.target.value)}
            disabled={presidentSaving || presidentOptions.length === 0}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          >
            {presidentOptions.length === 0 && <option value="">No models available</option>}
            {presidentOptions.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          {presidentSaving && <span className="text-xs text-gray-400 flex-shrink-0">Saving…</span>}
        </div>
      </div>

      <div className="mt-6">
        <div className="mb-3">
          <p className="text-sm font-semibold text-gray-800">Allowed Council Models</p>
          <p className="text-xs text-gray-500 mt-0.5">Turn a model off to hide it from Council member selection for this organization. The Council President is always available.</p>
        </div>
        <div className="border border-gray-200 rounded-xl divide-y divide-gray-100">
          {models.length === 0 && (
            <div className="p-5 text-sm text-gray-400">No selectable models found.</div>
          )}
          {models.map(m => {
            const enabled = !disabledModels.includes(m.id);
            return (
              <div key={m.id} className="flex items-center justify-between p-4 gap-6">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-purple-50 rounded-lg flex-shrink-0">
                    <Bot className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{m.name}</p>
                    <p className="text-[11px] text-gray-400">{m.provider}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    onClick={() => toggleModel(m.id)}
                    disabled={modelSaving === m.id}
                    className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${enabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${enabled ? 'translate-x-6' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CouncilSettings;
